import { useCallback, useEffect, useState } from 'react'
import { api, RequestFailed, type RedirectRow } from '../api'
import { Icon } from './Icon'

/**
 * Where the old addresses go.
 *
 * A rename already writes `redirect_from` onto the page that moved, which
 * covers what the product does to itself. This is the other half, and the
 * half a migration is made of: a site that used to live under `/docs/`, an
 * old section that became one overview page. Those are facts about the SITE.
 *
 * It had no screen at all. `redirects.yaml` was parsed, linted, written into
 * the built `_redirects` and honoured at the edge — and the only way to add
 * one was to edit YAML in a git repository, which is the thing this product
 * exists to avoid. A capability nobody can reach is not shipped.
 *
 * Each row says where it LANDS. A redirect pointing at a page nobody wrote is
 * the most common mistake in a migration and it is invisible in a file; here
 * it is a word on the row.
 */
const LANDS: Record<string, { label: string; tone: string }> = {
  page: { label: 'lands on a page', tone: 'ok' },
  nowhere: { label: 'lands nowhere', tone: 'warn' },
  offsite: { label: 'leaves the site', tone: 'quiet' },
  wildcard: { label: 'matches a section', tone: 'quiet' },
}

export function RedirectsScreen({
  branch,
  readOnly,
  onStaged,
  ensureDraft,
}: {
  /** The draft edits land on. Null means reading the live site. */
  branch: string | null
  readOnly: boolean
  onStaged: () => void
  /**
   * The draft this edit needs, made on demand.
   *
   * The same change the variables screen carries, for the same reason: a
   * screen that shows somebody their redirects and then asks them to press
   * "Start a draft" before they may touch one is a door in front of a room
   * they were already standing in. The editor makes its draft on the first
   * keystroke; adding a redirect is no different.
   */
  ensureDraft?: () => Promise<string | null>
}) {
  const [rows, setRows] = useState<RedirectRow[] | null>(null)
  const [invalid, setInvalid] = useState<string | null>(null)
  const [revision, setRevision] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [status, setStatus] = useState<301 | 302 | 308>(301)

  /**
   * Read the file back from the draft NAMED, not the one this render holds.
   *
   * The save that makes the draft writes to a branch this render has never
   * heard of; reloading `branch` would ask the live site and be told there
   * are no redirects on it, over one that had just been added.
   */
  const load = useCallback(
    async (on: string | null = branch) => {
      try {
        const result = await api.redirects(on ?? undefined)
        setRows(result.redirects)
        setInvalid(result.invalid)
        setRevision(result.revision)
        setError(null)
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e))
      }
    },
    [branch],
  )

  useEffect(() => void load(), [load])

  /**
   * The whole list, every time, with the revision it was read at.
   *
   * The same contract as variables: the surface holds all of it, so a whole
   * file write is what makes the diff a reviewer reads the actual change —
   * and two people editing at once get "changed underneath you" rather than a
   * silent overwrite.
   */
  const save = async (next: RedirectRow[]) => {
    // The draft, or the one this save makes.
    const on = branch ?? (await ensureDraft?.()) ?? null
    if (!on) return false
    setBusy(true)
    try {
      const out = await api.writeRedirects(on, next, revision ?? undefined)
      setRevision(out.revision)
      await load(on)
      onStaged()
      setError(null)
      return true
    } catch (e) {
      if (e instanceof RequestFailed && e.code === 'stale_page') {
        setError('Somebody else changed the redirects — reloaded, try again.')
        await load(on)
        return false
      }
      // The message is the parser's own, which names the redirect and says
      // what is wrong with it. Rewording it here would be a second, kinder
      // copy of a rule the publish gate applies strictly.
      setError(e instanceof Error ? e.message : String(e))
      return false
    } finally {
      setBusy(false)
    }
  }

  const add = async () => {
    const next = [...(rows ?? []), { from: from.trim(), to: to.trim(), status }]
    if (await save(next)) {
      setFrom('')
      setTo('')
      setStatus(301)
    }
  }

  const remove = async (at: number) => {
    await save((rows ?? []).filter((_, i) => i !== at))
  }

  if (!rows) {
    return (
      <div className="screen">
        <header className="screen__head">
          <h1>Redirects</h1>
        </header>
        {error ? (
          <p className="screen__error">
            <Icon name="alert" size={13} /> {error}
          </p>
        ) : (
          <div className="screen__loading" aria-busy="true">
            <p className="sr-only">Loading…</p>
            <span className="skeleton" />
            <span className="skeleton" />
            <span className="skeleton" />
          </div>
        )}
      </div>
    )
  }

  const broken = rows.filter((r) => r.lands === 'nowhere').length

  return (
    <div className="screen">
      <header className="screen__head">
        <h1>Redirects</h1>
        <p className="screen__sub">
          Addresses that used to work, and where they go now. A rename already redirects the page it
          moved; these are the ones a migration needs — a whole section that moved, or an old site
          that lived somewhere else. First match wins, top to bottom.
        </p>
      </header>

      {readOnly && (
        <p className="screen__warn">
          <Icon name="info" size={13} />
          {branch
            ? 'This draft is finished, so its redirects are read-only.'
            : 'You have view access to this site, so these are read-only.'}
        </p>
      )}

      {invalid && (
        <p className="screen__warn screen__warn--problem">
          <Icon name="alert" size={13} /> redirects.yaml is broken, so none of them are applied
          until it is fixed: {invalid}
        </p>
      )}

      {error && (
        <p className="screen__error">
          <Icon name="alert" size={13} /> {error}
        </p>
      )}

      {broken > 0 && (
        <p className="screen__warn screen__warn--problem">
          <Icon name="alert" size={13} />
          {broken === 1
            ? 'One redirect points at a page that does not exist. A reader following it lands on a 404.'
            : `${broken} redirects point at pages that do not exist. A reader following one lands on a 404.`}
        </p>
      )}

      {rows.length === 0 && !invalid ? (
        <p className="nothing">
          Nothing redirects yet. When you move a page the product writes one for you; add one here
          when an address that was never ours needs to land somewhere — an old site, or a section
          that moved before you arrived.
        </p>
      ) : (
        <ul className="redirects">
          {rows.map((row, at) => (
            <li key={`${row.from}-${at}`} className="redirects__row">
              <span className="redirects__from mono truncate">{row.from}</span>
              <Icon name="chevron-right" size={13} className="redirects__arrow" />
              <span className="redirects__to mono truncate">{row.to}</span>
              <span className={`redirects__lands redirects__lands--${row.lands ?? 'quiet'}`}>
                {LANDS[row.lands ?? 'wildcard']?.label ?? ''}
              </span>
              {row.status !== 301 && <span className="badge">{row.status}</span>}
              {!readOnly && (
                <button
                  className="btn btn--ghost btn--icon btn--sm"
                  disabled={busy}
                  data-tip={`Remove the redirect from ${row.from}`}
                  onClick={() => void remove(at)}
                >
                  <Icon name="trash" size={13} label="Remove" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {!readOnly && (
        <div className="redirects__new">
          <label className="field">
            <span className="label">Old address</span>
            <input
              className="input mono"
              placeholder="/docs/old-guide"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </label>
          <label className="field">
            <span className="label">Goes to</span>
            <input
              className="input mono"
              placeholder="/guides/install"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </label>
          <label className="field redirects__status">
            <span className="label">Kind</span>
            <select
              className="select"
              aria-label="Kind"
              value={status}
              onChange={(e) => setStatus(Number(e.target.value) as 301 | 302 | 308)}
            >
              <option value={301}>Moved for good (301)</option>
              <option value={302}>Moved for now (302)</option>
              <option value={308}>Moved for good, same method (308)</option>
            </select>
          </label>
          <button
            className="btn btn--tinted btn--sm"
            disabled={busy || from.trim() === '' || to.trim() === ''}
            onClick={() => void add()}
          >
            <Icon name="plus" size={13} />
            Add
          </button>
          <p className="field__note">
            A whole section moves with a trailing star: <code>/docs/*</code> to <code>/:splat</code>{' '}
            sends everything under the old path to the same place under the new one.
          </p>
        </div>
      )}
    </div>
  )
}
